import { useLisStages } from '@/hooks/useLisStages'
import { stageChoices, testCodeChoices } from '@/lib/lis/fieldStage'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// Radix refuses an empty-string item value, so "no stage" and "same as the
// column" travel as sentinels and are translated at the edge.
const ANY = '__any_stage__'
const INHERIT = '__inherit_stage__'
const ANY_TEST = '__any_test__'

/** What the test-code picker needs of a stage: its name and its tests. */
export type LisStageTests = {
  name: string
  active: boolean
  testCodes: ReadonlyArray<{ testCode: string }>
}

/** The tenant's stages with their test codes, for the test-code picker (z8nrz7g2wm). */
export function useLisStageTests(): LisStageTests[] {
  return useLisStages().data ?? []
}

/** The tenant's active LIS stage names, in their configured order. */
export function useActiveLisStageNames(): string[] {
  return (useLisStages().data ?? []).filter(s => s.active).map(s => s.name)
}

/**
 * Picks the LIS stage of a field, a table column or one cell.
 *
 * `value` is `undefined` for "inherit" (cells only, when `inheritLabel` is
 * given), `null` for no stage, or a stage name. A tenant with no stages sees
 * nothing at all unless something is already set, so forms that have nothing to
 * do with a laboratory are not cluttered with it.
 */
export function LisStageSelect({
  id,
  value,
  onChange,
  stages,
  inheritLabel,
  className,
  'aria-label': ariaLabel,
}: {
  id?: string
  value: string | null | undefined
  onChange: (next: string | null | undefined) => void
  stages: string[]
  /** Offer "same as the column", shown with this label. Cells only. */
  inheritLabel?: string
  className?: string
  'aria-label'?: string
}) {
  const choices = stageChoices(stages, value)
  if (choices.length === 0 && !inheritLabel) return null
  if (choices.length === 0 && value === undefined) return null

  const selected = value === undefined && inheritLabel ? INHERIT : value ? value : ANY
  return (
    <Select
      value={selected}
      onValueChange={v => onChange(v === INHERIT ? undefined : v === ANY ? null : v)}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={cn('h-9', className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {inheritLabel ? <SelectItem value={INHERIT}>{inheritLabel}</SelectItem> : null}
        <SelectItem value={ANY}>Any stage</SelectItem>
        {choices.map(name => (
          <SelectItem key={name} value={name}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * Picks the LIS test code a field, a column or one cell is connected to
 * (z8nrz7g2wm): the tests of its stage, or of every active stage when it has
 * none. Same value convention as `LisStageSelect`: `undefined` inherits the
 * column (cells only), `null` is none. Rendered only where there is
 * something to choose or something already set.
 */
export function LisTestCodeSelect({
  id,
  value,
  onChange,
  stages,
  stage,
  inheritLabel,
  className,
  'aria-label': ariaLabel,
}: {
  id?: string
  value: string | null | undefined
  onChange: (next: string | null | undefined) => void
  stages: readonly LisStageTests[]
  /** The target's stage: narrows the list to that stage's tests. */
  stage: string | null | undefined
  inheritLabel?: string
  className?: string
  'aria-label'?: string
}) {
  const choices = testCodeChoices(stages, stage, value)
  if (choices.length === 0 && !value) return null

  const selected = value === undefined && inheritLabel ? INHERIT : value ? value : ANY_TEST
  return (
    <Select
      value={selected}
      onValueChange={v => onChange(v === INHERIT ? undefined : v === ANY_TEST ? null : v)}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={cn('h-9 font-mono', className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {inheritLabel ? <SelectItem value={INHERIT}>{inheritLabel}</SelectItem> : null}
        <SelectItem value={ANY_TEST} className='font-sans'>None</SelectItem>
        {choices.map(code => (
          <SelectItem key={code} value={code} className='font-mono'>
            {code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
